import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getApiBase } from '../api/api';
import { getToken } from '../utils/session';

const chk = 'accent-sap-blue w-3.5 h-3.5';

const scrollTableWrap = 'max-h-[min(420px,55vh)] overflow-auto rounded-lg border border-sap-border/40 bg-white shadow-sm';
const th = 'px-3 py-2 text-left text-[10px] font-semibold text-sap-textLight uppercase tracking-wide bg-gray-100/95 border-b border-sap-border/60 sticky top-0 z-10 backdrop-blur-[2px]';
const td = 'px-3 py-1.5 text-[11px] text-sap-text border-b border-sap-border/15 align-top';

function ResultBlock({ title, data }) {
  if (data == null || (Array.isArray(data) && data.length === 0)) return null;
  return (
    <div className="mt-3">
      <div className="text-[10px] font-semibold text-sap-textLight uppercase tracking-wider mb-1">{title}</div>
      <pre className="text-[11px] bg-gray-50 border border-sap-border rounded-lg p-3 overflow-x-auto max-h-60 overflow-y-auto whitespace-pre-wrap">
        {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

function statusPill(text, tone) {
  const tones = {
    ok: 'bg-emerald-100 text-emerald-900 border-emerald-200/80',
    notice: 'bg-amber-100 text-amber-950 border-amber-200/80',
    bad: 'bg-red-100 text-red-900 border-red-200/80',
  };
  const c = tones[tone] || tones.ok;
  return (
    <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-md border whitespace-nowrap ${c}`}>
      {text}
    </span>
  );
}

const thSr = `${th} w-10 text-center`;
const thCb = `${th} w-11 text-center`;

function defaultDueDatePlusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Match server/CSV trimming so Blocked vs Ready selection is reliable. */
function normParentCode(c) {
  return String(c ?? '').trim();
}

/** Same component math as Production Order "Add New" after picking an item. */
async function postProductionOrderFromItemBom(itemId, parentCodeFromPreview, dueDate, headerPlannedQty) {
  const id = Number(itemId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error(`Invalid item id for "${parentCodeFromPreview}"`);
  }
  const { next: order_no } = await api.get('/production-orders/next-number');
  const data = await api.get(`/production-orders/item-bom/${id}`);
  const item = data.item || {};
  const masterNo = normParentCode(item.item_no);
  const expectNo = normParentCode(parentCodeFromPreview);
  if (!masterNo || masterNo !== expectNo) {
    throw new Error(
      `Item mismatch: row shows "${parentCodeFromPreview}" (id ${id}) but Item Master has "${item.item_no || '—'}" for that id — not creating this order.`
    );
  }
  const itemNo = String(item.item_no).trim();
  const bom = data.bom;
  const bomComponents = data.components || [];
  const baseQty = bom ? Number(bom.quantity) || 1 : 1;
  const plannedQty = Number(headerPlannedQty);
  const pq = Number.isFinite(plannedQty) && plannedQty > 0 ? plannedQty : baseQty;

  const components =
    bomComponents.length > 0
      ? bomComponents.map((c) => {
          const bc = Number(c.base_qty) || 0;
          const linePlanned = baseQty ? (pq * bc) / baseQty : 0;
          const uc = Number(c.unit_cost) || 0;
          return {
            line_type: 'Item',
            item_id: c.item_id || null,
            resource_id: null,
            description: c.description || '',
            base_qty: bc,
            base_ratio: baseQty,
            planned_qty: linePlanned,
            uom: c.uom || 'NOS',
            warehouse_id: c.warehouse_id || bom?.warehouse_id || null,
            issue_method: c.issue_method || 'Manual',
            unit_cost: uc,
            total_cost: linePlanned * uc,
          };
        })
      : undefined;

  const payload = {
    order_no,
    item_id: id,
    item_no: itemNo,
    description: item.description || '',
    planned_qty: pq,
    due_date: dueDate,
    start_date: null,
    status: 'Planned',
    prod_type: 'Standard',
    warehouse_id: bom?.warehouse_id || item.default_warehouse_id || null,
    linked_so_id: null,
    priority: 100,
    project_no: (bom?.project && String(bom.project).trim()) || null,
    remarks: 'Created from BOM CSV import',
    origin: 'Manual',
    uom_name: item.uom_name || 'PCS',
    customer: null,
    distr_rule: null,
    routing_date_calc: 'On Start Date',
    pick_pack_remarks: null,
    journal_remark: null,
    components,
  };

  return api.post('/production-orders', payload);
}

/** One table per product: preview + blocked + duplicate-BOM notice merged */
function ImportDryRunTable({ result, selectedParents, onToggleParent, onToggleAll }) {
  const preview = result.parentsPreview;
  if (!Array.isArray(preview) || preview.length === 0) return null;

  const blockedBy = new Map((result.ambiguousParents || []).map((a) => [normParentCode(a.parentCode), a]));
  const multiBy = new Map((result.multiBomWarnings || []).map((w) => [normParentCode(w.parentCode), w]));
  const allSelected =
    preview.length > 0 && preview.every((p) => selectedParents.has(normParentCode(p.parentCode)));
  const someSelected = preview.some((p) => selectedParents.has(normParentCode(p.parentCode)));

  return (
    <div className="mt-3 rounded-xl border border-sap-border/50 bg-gray-50/70 px-3 py-2.5">
      <div className="text-[11px] font-semibold text-sap-text mb-1">Products in your file</div>
      <p className="text-[11px] leading-relaxed text-sap-textLight mb-2">
        Each row is one finished product. <strong>#</strong> is the row number. Use <strong>Include</strong> to choose which parents are validated and imported; only checked rows are sent on the next run.
        <strong className="font-normal text-sap-text"> Components in file</strong> is how many lines from your spreadsheet will be saved for that product.
        <strong className="font-normal text-sap-text"> Status</strong> shows whether the import can proceed and what happens to duplicate bills.
      </p>
      <div className={scrollTableWrap}>
        <table className="w-full min-w-[720px] border-collapse">
          <thead>
            <tr>
              <th className={thSr}>#</th>
              <th className={thCb}>
                <span className="sr-only">Include</span>
                <input
                  type="checkbox"
                  className={chk}
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  title="Select or clear all"
                />
              </th>
              <th className={th}>Product (Item No.)</th>
              <th className={`${th} text-right w-[7rem]`}>Components in file</th>
              <th className={`${th} w-[6.5rem]`}>Status</th>
              <th className={th}>What this means</th>
            </tr>
          </thead>
          <tbody>
            {preview.map((p, idx) => {
              const blocked = blockedBy.get(normParentCode(p.parentCode));
              const multi = multiBy.get(normParentCode(p.parentCode));
              let tone = 'ok';
              let label = 'Ready';
              let explain = 'This product will be updated from your file.';
              if (blocked) {
                tone = 'bad';
                label = 'Blocked';
                if (blocked.reason === 'no_bom') {
                  explain =
                    'No Active Production bill exists. Turn on “Create Production BOM header if missing” or create the BOM in Bill of Materials.';
                } else {
                  const ids = Array.isArray(blocked.bom_ids) ? blocked.bom_ids.join(', ') : '';
                  explain = `More than one Active Production BOM (${ids ? `bills ${ids}` : 'multiple bills'}). Turn on “Use oldest Production BOM when several exist” or merge/remove extras in Bill of Materials.`;
                }
              } else if (multi) {
                tone = 'notice';
                label = 'Notice';
                const otherIds = multi.other_bom_ids || [];
                const others = otherIds.map((id) => `#${id}`).join(', ');
                explain =
                  otherIds.length > 0
                    ? `This file will replace lines on bill #${multi.chosen_bom_id} only. Other bill(s) (${others}) will not be changed—review them in Bill of Materials if you want a single bill.`
                    : `This file will replace lines on bill #${multi.chosen_bom_id} only.`;
              }
              return (
                <tr key={p.parentCode} className={tone === 'bad' ? 'bg-red-50/40' : tone === 'notice' ? 'bg-amber-50/35' : ''}>
                  <td className={`${td} text-center tabular-nums text-sap-textLight`}>{idx + 1}</td>
                  <td className={`${td} text-center`}>
                    <input
                      type="checkbox"
                      className={chk}
                      checked={selectedParents.has(normParentCode(p.parentCode))}
                      onChange={() => onToggleParent(p.parentCode)}
                      title="Include this product in validate / import"
                    />
                  </td>
                  <td className={`${td} font-mono text-[10px]`}>{p.parentCode}</td>
                  <td className={`${td} text-right tabular-nums`}>{p.lineCount}</td>
                  <td className={td}>{statusPill(label, tone)}</td>
                  <td className={`${td} text-sap-textLight leading-snug`}>{explain}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MissingItemsTable({ codes }) {
  if (!codes?.length) return null;
  return (
    <div className="mt-3 rounded-xl border border-red-200 bg-red-50/80 px-3 py-2.5">
      <div className="text-[11px] font-semibold text-red-900 mb-1">Unknown item codes</div>
      <p className="text-[11px] leading-relaxed text-red-950/90 mb-2">
        These values from your file do not match any active item in <strong>Item Master</strong> (or the item is deleted). Fix the CSV or add the items, then validate again.
      </p>
      <div className={scrollTableWrap}>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={th}>Item code in file</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => (
              <tr key={code}>
                <td className={`${td} font-mono text-[10px]`}>{code}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ApplyUpdatedTable({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="mt-3 rounded-xl border border-green-200/80 bg-green-50/60 px-3 py-2.5">
      <div className="text-[11px] font-semibold text-green-950 mb-2">Successfully saved</div>
      <div className={scrollTableWrap}>
        <table className="w-full min-w-[520px] border-collapse">
          <thead>
            <tr>
              <th className={th}>Product (Item No.)</th>
              <th className={`${th} text-right w-[5rem]`}>Bill #</th>
              <th className={`${th} text-right w-[7rem]`}>Lines saved</th>
              <th className={th}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const notes = [];
              if (r.createdHeader) notes.push('New Production BOM header created');
              if (r.usedFirstOfMultipleProductionBoms) notes.push('Duplicate Production BOMs existed—this bill was updated; others unchanged');
              const noteText = notes.length ? notes.join('. ') : '—';
              return (
                <tr key={`${r.parentCode}-${r.bom_id}`}>
                  <td className={`${td} font-mono text-[10px]`}>{r.parentCode}</td>
                  <td className={`${td} text-right tabular-nums`}>{r.bom_id}</td>
                  <td className={`${td} text-right tabular-nums`}>{r.linesWritten}</td>
                  <td className={`${td} text-sap-textLight`}>{noteText}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ApplyFailedTable({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2.5">
      <div className="text-[11px] font-semibold text-amber-950 mb-2">Not saved (errors)</div>
      <div className={scrollTableWrap}>
        <table className="w-full min-w-[480px] border-collapse">
          <thead>
            <tr>
              <th className={th}>Product (Item No.)</th>
              <th className={th}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.parentCode}>
                <td className={`${td} font-mono text-[10px] w-[40%]`}>{f.parentCode}</td>
                <td className={`${td} text-sap-textLight`}>{f.error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Plain-language + tables */
function FriendlyImportSummary({ result, selectedParents, onToggleParent, onToggleAll }) {
  const missing = result.missingItemNos;
  const blocked = result.ambiguousParents;
  return (
    <>
      <MissingItemsTable codes={missing} />
      {Array.isArray(blocked) && blocked.length > 0 && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50/80 px-3 py-2 text-[11px] text-red-950">
          <strong className="text-red-900">Validation failed.</strong>{' '}
          See the <strong>Blocked</strong> rows in the table below, change the options above, or fix data in Bill of Materials, then run <strong>Validate file</strong> again.
          You can also <strong>uncheck</strong> blocked parents and validate only the included rows.
        </div>
      )}
      <ImportDryRunTable
        result={result}
        selectedParents={selectedParents}
        onToggleParent={onToggleParent}
        onToggleAll={onToggleAll}
      />
    </>
  );
}

export default function BomCsvImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [createHeader, setCreateHeader] = useState(true);
  const [useOldestMultiBom, setUseOldestMultiBom] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [selectedParents, setSelectedParents] = useState(() => new Set());
  const parentSetKeyRef = useRef('');
  const [poDueDate, setPoDueDate] = useState(() => defaultDueDatePlusDays(14));
  const [poPlannedQty, setPoPlannedQty] = useState(1);
  const [poCreateBusy, setPoCreateBusy] = useState(false);
  const [poOutcome, setPoOutcome] = useState(null);

  const eligiblePreviewRows = useMemo(() => {
    const preview = result?.parentsPreview;
    if (!Array.isArray(preview) || !preview.length) return [];
    const blocked = new Set((result.ambiguousParents || []).map((a) => normParentCode(a.parentCode)));
    const selectedNorm = new Set([...selectedParents].map(normParentCode));
    return preview.filter((p) => {
      const code = normParentCode(p.parentCode);
      return selectedNorm.has(code) && !blocked.has(code);
    });
  }, [result, selectedParents]);

  const skippedBlockedSelected = useMemo(() => {
    const preview = result?.parentsPreview;
    if (!Array.isArray(preview) || !preview.length) return [];
    const blocked = new Set((result.ambiguousParents || []).map((a) => normParentCode(a.parentCode)));
    const selectedNorm = new Set([...selectedParents].map(normParentCode));
    return preview
      .filter((p) => {
        const code = normParentCode(p.parentCode);
        return selectedNorm.has(code) && blocked.has(code);
      })
      .map((p) => p.parentCode);
  }, [result, selectedParents]);

  /** One PO per parent is standard; user can limit to first table row only. */
  const [poOneOrderOnly, setPoOneOrderOnly] = useState(true);

  const previewRowsForPo = useMemo(() => {
    if (!poOneOrderOnly) return eligiblePreviewRows;
    return eligiblePreviewRows.length ? [eligiblePreviewRows[0]] : [];
  }, [eligiblePreviewRows, poOneOrderOnly]);

  const createProductionOrdersForSelection = async () => {
    setPoOutcome(null);
    setError('');
    if (!eligiblePreviewRows.length) {
      setError('Select at least one product that is not Blocked (Ready or Notice only).');
      return;
    }
    if (!poDueDate) {
      setError('Choose a due date for the new production orders.');
      return;
    }
    const pq = Number(poPlannedQty);
    if (!Number.isFinite(pq) || pq <= 0) {
      setError('Planned quantity must be a positive number.');
      return;
    }
    setPoCreateBusy(true);
    const created = [];
    const failed = [];
    try {
      for (const row of previewRowsForPo) {
        try {
          const out = await postProductionOrderFromItemBom(
            row.product_item_id,
            row.parentCode,
            poDueDate,
            pq
          );
          created.push({
            parentCode: row.parentCode,
            prod_order_id: out.prod_order_id,
            order_no: out.order_no,
          });
        } catch (err) {
          failed.push({ parentCode: row.parentCode, error: err.message || 'Failed' });
        }
      }
      setPoOutcome({ created, failed });
      if (failed.length && !created.length) {
        setError('No production orders were created — see errors below.');
      }
    } finally {
      setPoCreateBusy(false);
    }
  };

  const syncSelectionFromPreview = (data) => {
    const preview = data.parentsPreview || [];
    if (!preview.length) return;
    const key = preview.map((x) => normParentCode(x.parentCode)).sort().join('\0');
    if (parentSetKeyRef.current !== key) {
      parentSetKeyRef.current = key;
      setSelectedParents(new Set(preview.map((p) => normParentCode(p.parentCode))));
    } else {
      setSelectedParents((prev) => {
        const allowed = new Set(preview.map((p) => normParentCode(p.parentCode)));
        const next = new Set([...prev].map(normParentCode).filter((c) => allowed.has(c)));
        return next.size === 0 && allowed.size ? new Set(allowed) : next;
      });
    }
  };

  const downloadTemplate = () => {
    const base = getApiBase();
    const token = getToken();
    const url = `${base}/bom/import-csv/template`;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('Download failed'))))
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'bom-import-template.csv';
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => setError('Could not download template. Check API is running.'));
  };

  const downloadBomExport = (productionOnly) => {
    const base = getApiBase();
    const token = getToken();
    const qs = new URLSearchParams();
    if (productionOnly) qs.set('productionOnly', '1');
    qs.set('_', String(Date.now()));
    const url = `${base}/bom/export-csv?${qs.toString()}`;
    fetch(url, {
      cache: 'no-store',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('Export failed'))))
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = productionOnly ? 'bom-export-production-active.csv' : 'bom-export-all.csv';
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => setError('Could not export BOM CSV. Check API is running.'));
  };

  const run = async () => {
    setError('');
    setResult(null);
    if (!file) {
      setError('Choose a CSV file first.');
      return;
    }
    const hasPriorPreview = Boolean(parentSetKeyRef.current);
    if (hasPriorPreview && selectedParents.size === 0) {
      setError('Select at least one product in the table (use the Include checkboxes).');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (hasPriorPreview && selectedParents.size > 0) {
        fd.append('onlyParentCodes', JSON.stringify([...selectedParents]));
      }
      const qs = new URLSearchParams();
      if (dryRun) qs.set('dryRun', '1');
      if (createHeader) qs.set('createHeaderIfMissing', '1');
      if (useOldestMultiBom) qs.set('useFirstWhenMultipleProductionBoms', '1');
      const data = await api.postForm(`/bom/import-csv?${qs.toString()}`, fd);
      setResult(data);
      syncSelectionFromPreview(data);
    } catch (e) {
      setError(e.message || 'Import failed');
      if (e.data) {
        setResult(e.data);
        syncSelectionFromPreview(e.data);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate('/production/bom')}
            className="text-xs text-sap-blue hover:underline font-semibold">← Bill of Materials</button>
          <h1 className="page-title">BOM CSV import (DTW-style)</h1>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-sap-border/40 p-4 text-xs space-y-3">
        <p className="text-sap-text leading-relaxed">
          Upload a UTF-8 CSV with columns <strong>ParentCode</strong>, <strong>ChildCode</strong>, <strong>Quantity</strong>.
          Parent and child values must match <strong>Item No.</strong> in Item Master Data. This replaces all BOM lines for each parent
          (Active Production BOM), same as updating the master BOM in SAP B1.
        </p>
        <p className="text-[11px] text-sap-textLight leading-relaxed">
          <strong>Export from SQL:</strong> download every BOM component line already in the database in this same format.
          This is not a list of all items—only rows that exist on a bill of materials. Items never used on a BOM will not appear as{' '}
          <code className="text-[10px]">ParentCode</code>; they can still appear as <code className="text-[10px]">ChildCode</code>.
        </p>
        <ul className="list-disc pl-4 text-sap-textLight space-y-0.5 text-[11px]">
          <li>Run <strong>Dry run</strong> first — no database changes.</li>
          <li>If the same parent+child appears more than once (e.g. after exporting all BOM types), quantities are <strong>added together</strong> into one line.</li>
          <li>
            If a parent has more than one Active Production BOM, either fix duplicates in <strong>Bill of Materials</strong>, or leave{' '}
            <strong>Use oldest Production BOM when several exist</strong> checked (import updates the lowest <code className="text-[10px]">bom_id</code> only).
          </li>
        </ul>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button type="button" onClick={downloadTemplate}
            className="px-3 py-1.5 text-[11px] border border-sap-border rounded-lg hover:bg-gray-50 font-medium text-sap-text">
            Download sample CSV
          </button>
          <button type="button" onClick={() => downloadBomExport(false)}
            className="px-3 py-1.5 text-[11px] border border-sap-border rounded-lg hover:bg-gray-50 font-medium text-sap-text">
            Export all BOM lines (CSV)
          </button>
          <button type="button" onClick={() => downloadBomExport(true)}
            className="px-3 py-1.5 text-[11px] border border-sap-border rounded-lg hover:bg-gray-50 font-medium text-sap-text">
            Export Production + Active only
          </button>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="file" accept=".csv,text/csv" className="text-[11px]"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setResult(null);
                parentSetKeyRef.current = '';
                setSelectedParents(new Set());
                setPoOutcome(null);
              }} />
          </label>
        </div>

        <div className="flex flex-col gap-2 pt-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={dryRun} onChange={() => setDryRun(!dryRun)} className={chk} />
            <span>Dry run only (recommended)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={createHeader} onChange={() => setCreateHeader(!createHeader)} className={chk} />
            <span>Create Production BOM header if missing for parent</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={useOldestMultiBom} onChange={() => setUseOldestMultiBom(!useOldestMultiBom)} className={chk} />
            <span>Use oldest Production BOM when several exist for the same parent (lowest bom id)</span>
          </label>
        </div>

        <button type="button" disabled={busy}
          onClick={run}
          className="px-4 py-2 text-xs bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark font-semibold disabled:opacity-50">
          {busy ? 'Running…' : dryRun ? 'Validate file' : 'Import / update BOMs'}
        </button>

        {error && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
        )}

        {result && (
          <div className="border-t border-sap-border/40 pt-3 mt-2">
            <div
              className={`text-xs font-semibold mb-1 ${
                result.validationFailed
                  ? 'text-red-700'
                  : result.ok === false && !result.dryRun
                    ? 'text-amber-700'
                    : 'text-green-700'
              }`}
            >
              {result.message || (result.dryRun ? 'Dry run complete' : 'Import finished')}
            </div>
            {result.linesInFile != null && result.linesTotal != null && (
              <div className="text-[11px] text-sap-textLight">
                Rows in file: {result.linesInFile}
                {result.duplicateRowsMerged > 0
                  ? ` · After merging duplicates: ${result.linesTotal} · Merged: ${result.duplicateRowsMerged}`
                  : ''}
              </div>
            )}
            {result.parentsChecked != null && (
              <div className="text-[11px] text-sap-textLight">
                Parents: {result.parentsChecked}
                {Array.isArray(result.updated) ? ` · BOMs updated: ${result.updated.length}` : ''}
                {Array.isArray(result.failed) && result.failed.length > 0 ? ` · Failed: ${result.failed.length}` : ''}
              </div>
            )}

            <FriendlyImportSummary
              result={result}
              selectedParents={selectedParents}
              onToggleParent={(code) => {
                const n = normParentCode(code);
                setSelectedParents((prev) => {
                  const next = new Set([...prev].map(normParentCode));
                  if (next.has(n)) next.delete(n);
                  else next.add(n);
                  return next;
                });
              }}
              onToggleAll={(checked) => {
                const preview = result?.parentsPreview || [];
                if (checked) setSelectedParents(new Set(preview.map((p) => normParentCode(p.parentCode))));
                else setSelectedParents(new Set());
              }}
            />

            {result?.parentsPreview?.length > 0 && (
              <div className="mt-4 rounded-xl border border-sap-border/60 bg-[#F4F6F5] px-3 py-3">
                <div className="text-[11px] font-semibold text-sap-text mb-1">Create production orders</div>
                <p className="text-[10px] text-sap-textLight leading-relaxed mb-2">
                  Each production order in this system is for <strong>one finished product</strong> (one parent item) plus its BOM components. With{' '}
                  <strong>One production order only</strong> on (default), only the <strong>first</strong> checked Ready/Notice row in the table gets a PO.
                  Turn that off to create a <strong>separate</strong> PO for every checked product (e.g. three checked → three PROD numbers).
                  <strong className="font-normal text-amber-900"> Blocked</strong> rows never get a PO. Components on each PO come from the <strong>database BOM</strong>; import the CSV first if the PO must match the file.
                </p>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    className={chk}
                    checked={poOneOrderOnly}
                    onChange={() => setPoOneOrderOnly(!poOneOrderOnly)}
                  />
                  <span className="text-[11px] text-sap-text">
                    One production order only (first checked Ready/Notice row in table order)
                  </span>
                </label>
                {poOneOrderOnly && eligiblePreviewRows.length > 1 && (
                  <p className="text-[10px] text-amber-900 mb-1.5">
                    You have {eligiblePreviewRows.length} eligible products checked — only <strong>{eligiblePreviewRows[0].parentCode}</strong> will get a PO.
                    Uncheck the option above to create {eligiblePreviewRows.length} separate POs.
                  </p>
                )}
                {previewRowsForPo.length > 0 && (
                  <div className="text-[10px] text-sap-text mb-1.5">
                    <span className="font-semibold">Will create PO for: </span>
                    <span className="font-mono">{previewRowsForPo.map((r) => r.parentCode).join(', ')}</span>
                  </div>
                )}
                {eligiblePreviewRows.length > 0 && previewRowsForPo.length > 0 && !poOneOrderOnly && (
                  <div className="text-[10px] text-sap-textLight mb-1.5 italic">
                    {previewRowsForPo.length} separate production order{previewRowsForPo.length !== 1 ? 's' : ''} will be created.
                  </div>
                )}
                {skippedBlockedSelected.length > 0 && (
                  <div className="text-[10px] text-amber-950 mb-1.5">
                    <span className="font-semibold">Skipped (Blocked): </span>
                    <span className="font-mono">{skippedBlockedSelected.join(', ')}</span>
                  </div>
                )}
                <div className="flex flex-wrap items-end gap-3 mb-2">
                  <label className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-medium text-sap-textLight">Due date</span>
                    <input
                      type="date"
                      value={poDueDate}
                      onChange={(e) => setPoDueDate(e.target.value)}
                      className="text-[11px] border border-sap-border rounded-md px-2 py-1 bg-white"
                    />
                  </label>
                  <label className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-medium text-sap-textLight">Planned qty (finished good)</span>
                    <input
                      type="number"
                      min={0.0001}
                      step="any"
                      value={poPlannedQty}
                      onChange={(e) => setPoPlannedQty(e.target.value)}
                      className="text-[11px] border border-sap-border rounded-md px-2 py-1 w-24 bg-white"
                    />
                  </label>
                  <button
                    type="button"
                    disabled={poCreateBusy || previewRowsForPo.length === 0}
                    onClick={createProductionOrdersForSelection}
                    className="px-3 py-1.5 text-[11px] font-semibold rounded-lg bg-sap-headerFrom text-white hover:bg-sap-blueDeeper disabled:opacity-45"
                  >
                    {poCreateBusy
                      ? 'Creating…'
                      : poOneOrderOnly && previewRowsForPo.length === 1
                        ? `Create 1 production order (${previewRowsForPo[0].parentCode})`
                        : `Create production orders (${previewRowsForPo.length})`}
                  </button>
                </div>
                {poOutcome && (
                  <div className="text-[11px] space-y-1.5 mt-2 pt-2 border-t border-sap-border/40">
                    {poOutcome.created.length > 0 && (
                      <div className="text-green-900">
                        <span className="font-semibold">Created: </span>
                        {poOutcome.created.map((c, i) => (
                          <span key={`${c.parentCode}-${c.prod_order_id}`}>
                            {i > 0 ? ' · ' : ''}
                            <button
                              type="button"
                              className="text-sap-blue underline font-medium"
                              onClick={() => navigate(`/production-order/${c.prod_order_id}`)}
                            >
                              {c.order_no}
                            </button>
                            <span className="text-sap-text"> ({c.parentCode})</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {poOutcome.failed.length > 0 && (
                      <div className="text-amber-950">
                        <span className="font-semibold">Failed: </span>
                        {poOutcome.failed.map((f) => `${f.parentCode}: ${f.error}`).join(' · ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!result.dryRun && <ApplyUpdatedTable rows={result.updated} />}
            {!result.dryRun && <ApplyFailedTable rows={result.failed} />}

            <details className="mt-4 group text-[11px] border border-sap-border/50 rounded-lg bg-white/80">
              <summary className="cursor-pointer select-none px-3 py-2 font-semibold text-sap-blue hover:bg-gray-50/80 rounded-lg list-none [&::-webkit-details-marker]:hidden flex items-center gap-2">
                <span className="text-sap-textLight group-open:rotate-90 transition-transform inline-block">▸</span>
                Technical details (JSON — for support / IT)
              </summary>
              <div className="px-3 pb-3 border-t border-sap-border/30 pt-2">
                <ResultBlock title="Parents preview" data={result.parentsPreview} />
                <ResultBlock title="Multiple Production BOMs" data={result.multiBomWarnings} />
                <ResultBlock title="Blocked parents (dry run)" data={result.ambiguousParents} />
                <ResultBlock title="Missing item numbers" data={result.missingItemNos} />
                <ResultBlock title="Updated BOMs" data={result.updated} />
                <ResultBlock title="Failed parents" data={result.failed} />
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
